import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type ActionMenuItem = {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  tone?: "default" | "success" | "danger";
};

const TONE: Record<NonNullable<ActionMenuItem["tone"]>, string> = {
  default: "text-[var(--color-navy-700)]",
  success: "text-[#16a34a]",
  danger: "text-[var(--color-danger)]",
};

/**
 * Bouton « ··· » (44×44) qui ouvre une liste d'actions alignée à droite. Se ferme au clic
 * extérieur, à Échap et après le choix d'une action. Dropdown en z-40 : au-dessus de la barre
 * d'onglets (z-30), sous les modales (z-50).
 */
export function ActionMenu({ items }: { items: ActionMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--color-navy-700)] hover:bg-[var(--color-muted)]"
        onClick={() => setOpen((o) => !o)}
      >
        <MoreHorizontal size={22} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-1 min-w-52 overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white py-1 shadow-[0_8px_24px_rgb(0_0_0/0.14)]"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={cn(
                "flex h-11 w-full items-center gap-3 px-4 text-left text-[15px] font-medium whitespace-nowrap hover:bg-[var(--color-muted)]",
                TONE[item.tone ?? "default"],
              )}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              <item.icon size={17} aria-hidden />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
